import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Bell, BellOff, BellRing, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import usePushNotifications from '../lib/usePushNotifications';

export const PushNotificationSettings = () => {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  const handleToggle = async () => {
    console.log('Toggle clicked - isSubscribed:', isSubscribed, 'permission:', permission);
    
    if (isSubscribed) {
      const result = await unsubscribe();
      if (result.success) {
        toast.success('Push notifications disabled');
      } else {
        toast.error(result.error || 'Failed to disable notifications');
      }
    } else {
      const result = await subscribe();
      if (result.success) {
        toast.success('Push notifications enabled! You will now receive alerts for new requests.');
      } else {
        if (permission === 'denied') {
          toast.error('Please enable notifications in your browser settings');
        } else {
          toast.error(result.error || 'Failed to enable notifications');
        }
      }
    }
  };

  const handleTestNotification = async () => {
    const result = await sendTestNotification();
    if (result.success) {
      toast.success('Test notification sent! Check your notifications.');
    } else {
      toast.error(result.error || 'Failed to send test notification');
    }
  };

  if (!isSupported) {
    return (
      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertCircle className="w-5 h-5 text-amber-600" />
        <div>
          <p className="font-medium text-amber-800">Push notifications not supported</p>
          <p className="text-sm text-amber-600">
            Your browser doesn&apos;t support push notifications. Try using Chrome, Firefox, or Edge.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="push-notification-settings">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
        <Bell className="w-5 h-5 text-indigo-600" />
        Push Notifications
      </h3>
      <p className="text-sm text-slate-500">
        Receive instant alerts on your device when you get new guide requests
      </p>

      {/* Status Banner */}
      {permission === 'denied' && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <BellOff className="w-5 h-5 text-red-600" />
          <div>
            <p className="font-medium text-red-800">Notifications Blocked</p>
            <p className="text-sm text-red-600">
              Please enable notifications in your browser settings to receive alerts.
            </p>
          </div>
        </div>
      )}

      {isSubscribed && (
        <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <div>
            <p className="font-medium text-emerald-800">Notifications Active</p>
            <p className="text-sm text-emerald-600">
              You&apos;ll receive alerts for new requests and updates.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Toggle Switch */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <BellRing className="w-5 h-5 text-indigo-600" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-400" />
          )}
          <div>
            <Label htmlFor="push-toggle" className="font-medium cursor-pointer">
              {loading ? 'Loading...' : isSubscribed ? 'Notifications Enabled' : 'Enable Notifications'}
            </Label>
            <p className="text-sm text-slate-500">
              {loading 
                ? 'Checking status...'
                : isSubscribed 
                  ? 'You will receive push notifications for new requests'
                  : 'Turn on to receive instant alerts'}
            </p>
          </div>
        </div>
        <Switch
          id="push-toggle"
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={loading}
          data-testid="push-toggle-switch"
          className="data-[state=checked]:bg-indigo-600"
        />
      </div>

      {/* Test Notification Button */}
      {isSubscribed && (
        <Button
          variant="outline"
          onClick={handleTestNotification}
          disabled={loading}
          className="w-full"
          data-testid="test-push-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          Send Test Notification
        </Button>
      )}

      {/* Info Text */}
      <p className="text-xs text-slate-500 text-center">
        Push notifications work even when the app is closed.
      </p>
    </div>
  );
};

export default PushNotificationSettings;
